import React from 'react'
import strings from 'src/strings'
import { Form, Field } from 'react-final-form'
import { required } from 'src/lib/form'
import TextField from 'src/components/FormFields/TextField'
import { get } from 'lodash'
import {
  useCreateNewClassGroupMutation,
  ClassGroupsDocument,
  ClassGroupsQuery,
} from 'src/graphql-generated'
import { setPreferredClassGroup } from 'src/lib/userPreferenceSettings'
import { dashboardPath } from 'src/lib/paths'
import { Title1, Body } from './text'
import { Flex, Box } from './Box'
import Button from './Button'
import { Modal, ModalProps } from './Modal'

type ConfirmationModalProps = ModalProps & {
  cancelOnClick: () => void
}

const CreateClassGroupModal: React.FunctionComponent<
  ConfirmationModalProps
> = ({ open, cancelOnClick }) => {
  const mutate = useCreateNewClassGroupMutation({
    update: (cache, { data }) => {
      if (!data) {
        return
      }

      const cachedClassGroups = get(
        cache.readQuery<ClassGroupsQuery>({
          query: ClassGroupsDocument,
        }),
        'viewerClassGroups',
        []
      )

      cache.writeQuery({
        query: ClassGroupsDocument,
        data: {
          viewerClassGroups: [...cachedClassGroups, data.createNewClassGroup],
        },
      })
    },
  })

  return (
    <Modal open={open}>
      <Flex
        maxWidth="410px"
        width="90vw"
        m="8"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        data-cy="modal"
      >
        <Title1 color="purple">{strings.createClassGroupModal.title}</Title1>

        <Box my="4" width="100%">
          <Form
            onSubmit={async ({ name, description }: any) => {
              if (!name) {
                throw new Error('No name provided')
              }

              const returnValue = await mutate({
                variables: {
                  name,
                  description,
                },
              })

              if (returnValue && returnValue.data) {
                setPreferredClassGroup(returnValue.data.createNewClassGroup.id)
              }

              window.location.assign(dashboardPath())
            }}
            render={({ handleSubmit }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Flex flexDirection="column">
                    <Body textAlign="center" mb="5">
                      {strings.TeacherSignupForm.subtitle}
                    </Body>

                    <Box>
                      <Field validate={required} name="name">
                        {field => (
                          <TextField
                            field={field}
                            label={strings.TeacherSignupForm.classNameLabel}
                            placeholder={
                              strings.TeacherSignupForm.classNamePlaceholder
                            }
                            maxLength={50}
                          />
                        )}
                      </Field>
                      <Field validate={required} name="description">
                        {field => (
                          <TextField
                            field={field}
                            label={
                              strings.TeacherSignupForm.classDescriptionLabel
                            }
                            placeholder={
                              strings.TeacherSignupForm
                                .classDescriptionPlaceholder
                            }
                            maxLength={100}
                          />
                        )}
                      </Field>
                    </Box>

                    <Flex
                      mt="5"
                      flexDirection={['column', 'row']}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Box mr={[0, 5]} mb={[5, 0]}>
                        <Button
                          data-cy="cancel-createClassGroup"
                          type="button"
                          minWidth="177px"
                          onClick={cancelOnClick}
                          px={undefined}
                          variant="secondary"
                        >
                          <Flex flexDirection="row" justifyContent="center">
                            {strings.createClassGroupModal.cancelButtonText}
                          </Flex>
                        </Button>
                      </Box>
                      <Box>
                        <Button
                          data-cy="confirm-createClassGroup"
                          type="submit"
                          variant="primary"
                          minWidth="177px"
                        >
                          <Flex flexDirection="row" justifyContent="center">
                            {strings.createClassGroupModal.createButtonText}
                          </Flex>
                        </Button>
                      </Box>
                    </Flex>
                  </Flex>
                </form>
              )
            }}
          />
        </Box>
      </Flex>
    </Modal>
  )
}

export default CreateClassGroupModal
